<template>
  <section class="pb-1">
    <div class="forecast-dropdown">
      <b-dropdown
        variant="outline-dark"
        class="bg-white"
        size="sm"
        :disabled="isLoading || noDataStored"
        toggle-class="text-decoration-none"
      >
        <template v-slot:button-content>
          {{ selectedDays.text || $tk('ClientPortal.Form.FilterDays') }}
        </template>
        <b-dropdown-item
          v-for="(value, key) in forecastWrapper.availableDays"
          :key="value"
          @click="selectDays(value, key)"
        >
          <span
            class="d-flex justify-content-between"
            :class="selectedDays.id === value ? 'text-primary': ''"
          >
            {{ key }}
            <GjIcon
              v-if="selectedDays.id === value"
              name="Check"
            />
          </span>
        </b-dropdown-item>
      </b-dropdown>
      <div>
        <filter-wrapper
          :properties="selectedProperties"
          :width-offset="380"
          :has-click-event="false"
          :has-filter-number="false"
          :has-sub-menus="false"
          @cleared="clearFilters"
        />
        <b-dropdown
          variant="outline-dark"
          class="bg-white"
          size="sm"
          :disabled="isLoading || noDataStored"
          toggle-class="text-decoration-none"
        >
          <template v-slot:button-content>
            {{ $tk('ClientPortal.Form.Properties') }}
          </template>
          <b-dropdown-item
            v-for="(property, index) in forecastWrapper.properties"
            :key="index"
            @click="selectProperties(property)"
          >
            <span
              class="d-flex justify-content-between"
              :class="selectedProperties.includes(property) ? 'text-primary': ''"
            >
              {{ property.name }}
              <GjIcon
                v-if="selectedProperties.includes(property)"
                class="ml-50"
                name="Check"
              />
            </span>
          </b-dropdown-item>
        </b-dropdown>
        <b-button
          variant="primary"
          class="ml-1"
          size="sm"
          :disabled="!selectedDays.id || isLoading || noDataStored"
          @click="applyFilters"
        >
          {{ $tk('ClientPortal.MasterTable.Apply') }}
        </b-button>
      </div>
    </div>
    <section v-if="!filterSelected || noDataStored || isLoading">
      <b-card class="no-graph-card">
        <b-spinner
          v-if="isLoading"
          variant="primary"
          label="Spinning"
        />
        <div v-else>
          <GjIcon
            name="Monitoring"
            size="54"
            class="mb-1"
          />
          <div v-if="noDataStored">
            <span>{{ $tk('ClientPortal.Forecast.NoDataStored') }}</span>
            <span>{{ $tk('ClientPortal.Forecast.MandatoryForecast') }}</span>
          </div>
          <span v-else-if="!filterSelected">{{ $tk('ClientPortal.Forecast.PleaseSelectFiltersAboveInOrderToStartForecasting') }}</span>
        </div>
      </b-card>
    </section>
    <section
      v-else
    >
      <div>
        <b-card
          class="mt-2"
          no-body
        >
          <b-row class="m-0 p-0">
            <b-col
              v-for="(value, key) in forecast.summary"
              :key="key"
              class="forecast-info"
            >
              <small class="text-muted mb-25 text-capitalize">
                {{ key }}
              </small>
              <span class="forecast-info__value">
                {{ formatValue(value) }}
              </span>
            </b-col>
          </b-row>
        </b-card>
      </div>
      <div>
        <ForecastGraphsCard
          :title="$tk('Forecast.ChartLabel.Impressions')"
          :chart-data="forecast.impressions"
        />
        <ForecastGraphsCard
          :title="$tk('Forecast.ChartLabel.ViewableImpressions')"
          :chart-data="forecast.views"
        />
        <ForecastGraphsCard
          :title="$tk('Forecast.ChartLabel.Actions')"
          :chart-data="forecast.actions"
        />
        <ForecastGraphsCard
          :title="$tk('Forecast.ChartLabel.Clicks')"
          :chart-data="forecast.clicks"
        />
      </div>
    </section>
  </section>
</template>

<script>
import ForecastGraphsCard from '@/components/ForecastGraphsCard.vue'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import {
  ref, onMounted, watch, computed,
} from '@vue/composition-api'
import ForecastSevice from '@/api/client/forecast.service'
import notification from '@/mixins/toastification'
import FilterWrapper from '@/components/FilterWrapper.vue'

export default {
  components: {
    ForecastGraphsCard,
    GjIcon,
    FilterWrapper,
  },
  setup(_, context) {
    const noDataStored = ref(false)
    const filterSelected = ref(false)
    const isLoading = ref(true)
    const forecast = ref({})
    const forecastWrapper = ref({})
    const selectedDays = ref({
      text: null,
      id: null,
    })
    const selectedProperties = ref([])

    const accountId = computed(() => context.root.$route.params.accountId)

    const fatchData = async () => {
      isLoading.value = true
      try {
        forecastWrapper.value = await ForecastSevice.prepareForecastWrapper(accountId.value)
        if (!forecastWrapper.value.properties.length) {
          noDataStored.value = true
        } else {
          noDataStored.value = false
        }
      } catch (err) {
        notification.methods.error(err.message)
      }
      isLoading.value = false
    }

    onMounted(async () => {
      fatchData()
    })

    watch(accountId, async () => {
      filterSelected.value = false
      forecast.value = {}
      selectedDays.value = {
        text: null,
        id: null,
      }
      selectedProperties.value = []
      fatchData()
    })

    const formatValue = amount => {
      const amt = Number(amount)
      // eslint-disable-next-line no-mixed-operators
      return amt && amt.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '0'
    }

    const selectDays = (value, key) => {
      selectedDays.value.text = key
      selectedDays.value.id = value
    }

    const selectProperties = property => {
      const index = selectedProperties.value.findIndex(x => x.id === property.id)
      if (index === -1) {
        selectedProperties.value.push(property)
      } else {
        selectedProperties.value.splice(index, 1)
      }
    }

    const applyFilters = async () => {
      isLoading.value = true
      try {
        forecast.value = await ForecastSevice.getForecast(accountId.value,
          {
            Period: selectedDays.value.id,
            PropertyIds: selectedProperties.value.length ? selectedProperties.value.map(x => x.id) : forecastWrapper.value.properties.map(x => x.id),
          })
      } catch (err) {
        notification.methods.error(err.message)
      } finally {
        filterSelected.value = true
        isLoading.value = false
      }
    }

    const clearFilters = propertyIndex => {
      selectedProperties.value.splice(propertyIndex, 1)
    }

    return {
      filterSelected,
      noDataStored,
      forecast,
      formatValue,
      forecastWrapper,
      selectedDays,
      selectDays,
      selectProperties,
      selectedProperties,
      applyFilters,
      isLoading,
      clearFilters,
    }
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/views/client/properties/forecasting.scss';
</style>
